import api from './api';
import { PaymentMethodType } from './payments';

export interface CreditBundle {
  id: string;
  name: string;
  price: number;
  basicCredits: number;
  enhancedCredits: number;
  bonusPercent: number;
  popular?: boolean;
  badge?: string;
  isActive?: boolean;
}

export interface CreditPurchaseResponse {
  purchaseId: string;
  paymentId: string;
  checkoutUrl: string;
  bundle: {
    name: string;
    price: number;
    basicCredits: number;
    enhancedCredits: number;
  };
}

export interface PurchaseStatus {
  purchaseStatus: 'pending' | 'completed' | 'failed';
  credits?: {
    basicCredits: number;
    enhancedCredits: number;
  };
  bundleName?: string;
  pricePaid?: number;
}

class CreditService {
  /**
   * Get all available credit bundles
   */
  async getBundles(): Promise<CreditBundle[]> {
    const response = await api.request('/api/credits/bundles');
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch bundles');
    }
    return response.data;
  }

  /**
   * Purchase a credit bundle
   */
  async purchaseBundle(params: {
    bundleId: string;
    paymentMethod: PaymentMethodType;
    successUrl: string;
    failureUrl: string;
  }): Promise<CreditPurchaseResponse> {
    const response = await api.request('/api/credits/purchase', {
      method: 'POST',
      body: JSON.stringify(params),
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to purchase bundle');
    }
    return response.data;
  }

  /**
   * Check purchase status and complete if paid
   */
  async getPurchaseStatus(purchaseId: string): Promise<PurchaseStatus> {
    const response = await api.request(`/api/credits/purchase/${purchaseId}/status`);
    if (!response.success) {
      throw new Error(response.error || 'Failed to check purchase status');
    }
    return response.data;
  }

  /**
   * Get user's credit balance
   */
  async getBalance(): Promise<{ basicCredits: number; enhancedCredits: number }> {
    const response = await api.request('/api/credits/balance');
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch balance');
    }
    return response.data;
  }
}

export const creditService = new CreditService();
